import { createSearchParams, useNavigate } from "react-router-dom";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useState } from "react";
import { BaseLogo, getLogoOrgId } from "../../../../core/models/entities/BaseLogo";
import { Organization } from "../../../../core/models/entities/Organization";
import { LogoCommit } from "../../../../data/models/bodies/LogoCommit";
import { useOrderActionHandler } from "../../order-create/hooks/useOrderActionsHandler";
import { Verdict } from "../../../components/ApprovalVoting/approval-voting";
import {
  useErrorToast,
  useSuccessToast,
  withToasts,
  WithToastsOptions,
} from "../../../components/Toast/Toast";
import { useCartHidden } from "../../../../hooks/useCartHidden";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { getAllQuery } from "../../logo-editor/logo-editor.screen";
import { vendorLookupQuery } from "../../../../utils/models/vendorListQuery";
import { actAsCacheKey } from "../../../../contexts/actAsStore";
import { enviroment } from "../../../../enviroment";
import {
  LOGOS_SKIPPED_NOT_APPROVED_KEY,
  splitOrderableLogos,
} from "../../../../utils/functions/orderability";
import {
  MAX_SIZE_CLAMP_TOAST_MS,
  maxSizeClampSuccessText,
  readMaxSizeClamp,
  wasClampedByServer,
} from "../../../../utils/functions/maxSizeClamp";
import { OversizeAddOutcome } from "../../../../utils/functions/oversizeSheet";
import {
  trackLogoCreated,
  trackLogoUploaded,
} from "../../../../utils/functions/analytics";
import {
  dropRevokedUploadUrl,
  logoDetailKey,
  markDetailHighResPending,
} from "../../../../data/repositories/logoDetailCache";
import { runCommitAndInitiateApproval } from "./initiateApprovalFlow";
import {
  hasGenuineHighRes,
  sourceSharpUrl,
  withSharpPlaceholder,
} from "../../logo-editor/functions/keepSharp";
import { isHighResPending } from "../../../../hooks/useHealPendingHighRes";
import {
  isLogoUploadPending,
  isUploadKindPending,
} from "../../../../utils/functions/productPending";
import {
  previewUrlForFile,
  registerPendingUploadFile,
  releasePendingUploadFile,
  seedPendingUpload,
} from "../../logo-editor/functions/uploadPending";
import {
  isSingleRequestRouteMissing,
  markSingleRequestRouteMissing,
  shouldTrySingleRequestUpload,
} from "../../../../utils/functions/uploadSingleRequest";

// Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED (paired with the API's
// LogoMethodMaxSizeEnabled): the API scales a clone whose source is over the
// method's max size down, and says so in the copy answer itself
// (maxSizeClamp, flow "clone" - synchronous and instant clone alike). THAT
// marker decides, never a guess from the sizes: a clamped clone must not get
// the source's size "restored" (the resize would only be scaled down again
// and cost one supplier call) nor have the editor seeded with it. No marker
// (nothing was scaled, an older API, the API's flag off) = false = today's
// restore. Flag off = always false.
export function isCloneSizeClampedByServer(copy: BaseLogo): boolean {
  return wasClampedByServer(copy);
}

// Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: the toast options of a flow
// whose answer may say "the server scaled this logo down" (maxSizeClamp).
// Then the flow's success toast is replaced by ONE toast carrying the
// flow's own success text + what happened to the size - never two toasts.
// `pick` hands over the answer to look at (the transaction's result, or
// the answer a flow without a result kept aside); `allow` is an existing
// showSuccess rule that still gets the last word (the oversize-sheet
// guard). No marker / flag off = exactly the classic success toast.
export function clampAwareToasts<T>(
  successMessage: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  pick: (result: T) => unknown,
  allow?: (result: T) => boolean
): WithToastsOptions<T> | undefined {
  if (!enviroment.logoMethodMaxSizeEnabled) {
    return allow ? { showSuccess: allow } : undefined;
  }
  return {
    showSuccess: (result: T) => {
      if (allow && !allow(result)) return false;
      const clamp = readMaxSizeClamp(pick(result));
      if (!clamp) return true;
      useSuccessToast(
        maxSizeClampSuccessText(successMessage, clamp, t),
        MAX_SIZE_CLAMP_TOAST_MS
      );
      return false;
    },
  };
}

export function useLogoActionHandler() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logoRepository, organizationRepository } = useRepositoriesContext();
  const queryClient = useQueryClient();
  const { handleLogoAddOrderLine, handleLogoAddOrderLines } =
    useOrderActionHandler();
  const cartHidden = useCartHidden();

  const [isLoading, setIsLoading] = useState(false);

  // withToasts for a flow whose answer may carry the server's maxSizeClamp
  // (see clampAwareToasts). Without options - the flag off - the call is
  // today's five-argument call exactly.
  function withClampToasts<T>(
    transaction: () => Promise<T>,
    loadingMessage: string,
    successMessage: string,
    errorMessage: string,
    pick: (result: T) => unknown,
    allow?: (result: T) => boolean
  ): Promise<T> {
    const options = clampAwareToasts<T>(successMessage, t, pick, allow);
    return options
      ? withToasts(
          transaction,
          loadingMessage,
          successMessage,
          errorMessage,
          setIsLoading,
          options
        )
      : withToasts(
          transaction,
          loadingMessage,
          successMessage,
          errorMessage,
          setIsLoading
        );
  }

  function handleGoToCreateNewLogo() {
    navigate("/logo/create");
  }

  // Oversize sheet guard (flag REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED): the
  // cart handler answers {added, refused}; a refused logo was NOT put in the
  // cart (its dialog is open), so no "Added to cart" for it. Flag off (or a
  // mocked handler answering nothing) = refused 0 = today's toast.
  function refusedAll(outcome: OversizeAddOutcome | undefined): boolean {
    return !!outcome && outcome.refused > 0 && outcome.added === 0;
  }

  function handleAddToCart(logo: BaseLogo, quantity: number): OversizeAddOutcome | undefined {
    if (cartHidden) {
      toast.error(t("logoList.cartNotAvailable"));
      return undefined;
    }
    const outcome = handleLogoAddOrderLine(logo, quantity, 0);
    if (refusedAll(outcome)) return outcome;
    useSuccessToast(t("logoList.addedToCart"));
    return outcome;
  }

  function handleAddItemsToCart(logos: BaseLogo[], quantity: number) {
    if (cartHidden) {
      toast.error(t("logoList.cartNotAvailable"));
      return;
    }
    // Flag designOrderabilityGuardEnabled (#724425): bulk "Add to cart"
    // skips every logo that may not be ordered (rule §1: status done only)
    // and says how many («n logos skipped – approval pending»); the
    // orderable rest goes in as today. All skipped = nothing added, no
    // success toast. Flag off = the selection untouched.
    if (enviroment.designOrderabilityGuardEnabled) {
      const split = splitOrderableLogos(logos);
      if (split.skipped.length > 0) {
        toast.error(
          t(LOGOS_SKIPPED_NOT_APPROVED_KEY, { count: split.skipped.length }),
        );
      }
      if (split.orderable.length === 0) return;
      logos = split.orderable;
    }
    const outcome = handleLogoAddOrderLines(logos, quantity, 0);
    if (refusedAll(outcome)) return;
    if (outcome && outcome.refused > 0) {
      // Some went in, some were refused: say how many, not "Added to cart".
      useSuccessToast(
        t("oversizeSheet.addedPartially", {
          added: outcome.added,
          total: outcome.added + outcome.refused,
        }),
      );
      return;
    }
    useSuccessToast(t("logoList.addedToCart"));
  }

  function handleGoToEditLogo(logo: BaseLogo) {
    navigate({
      pathname: "/logo/create",
      search: createSearchParams({
        ["logo"]: "" + logo.id,
        ["organization"]: "" + getLogoOrgId(logo),
      }).toString(),
    });
  }

  function handleUpdateLogoColor(
    orgId: number,
    logoId: number,
    changeToColor: ChangeLogoColors
  ) {
    //console.log('Updating logo colors:', {
    //  orgId,
    //  logoId,
    //  colorChanges: changeToColor.colorChanges
    //});

    async function transaction() {
      return await logoRepository.changeColors(orgId, logoId, changeToColor);
    }

    return withToasts(
      transaction,
      t("logoList.updatingLogoColor"),
      t("logoList.logoColorUpdated"),
      t("logoList.logoColorUpdateFailed"),
      setIsLoading
    );
  }

  function handleChangeTitle(orgId: number, logoId: number, title: string) {
    async function transaction() {
      return await logoRepository.changeTitle(orgId, logoId, title);
    }

    return withToasts(
      transaction,
      t("logoList.updatingLogoTitle"),
      t("logoList.logoTitleUpdated"),
      t("logoList.logoTitleUpdateFailed"),
      setIsLoading
    );
  }

  function handleUpdateLogo(orgId: number, logo: BaseLogo) {
    async function transaction() {
      return await logoRepository.update(orgId, logo);
    }

    return withToasts(
      transaction,
      t("logoList.updatingLogo"),
      t("logoList.logoUpdated"),
      t("logoList.logoUpdateFailed"),
      setIsLoading
    );
  }

  function handleResizeLogo(orgId: number, logo: BaseLogo) {
    async function transaction() {
      return await logoRepository.resize(orgId, logo);
    }

    // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: a requested size above the
    // method's max size comes back scaled down - one toast says so.
    return withClampToasts<BaseLogo>(
      transaction,
      t("logoList.resizingLogo"),
      t("logoList.logoResized"),
      t("logoList.logoResizeFailed"),
      (logo) => logo
    );
  }

  // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED - the design editor's
  // "Change size" after a method change that is not saved yet. The API's
  // resize scales down to the max size of the method the ROW holds, so a
  // size typed for the newly chosen method would be judged by the old one
  // and the fields would jump. The method is saved first (the same PUT
  // update "Save changes & approve" would send - it is then a no-change
  // there), and the resize is judged by the method the user sees. ONE
  // toast for the two calls. `onMethodSaved` hands the caller the update's
  // answer before the resize runs, so a failed resize leaves the tool on
  // the saved method.
  function handleSaveMethodThenResizeLogo(
    orgId: number,
    logo: BaseLogo,
    size: { widthMm: number; heightMm: number },
    onMethodSaved?: (saved: BaseLogo) => void
  ) {
    async function transaction() {
      const saved = await logoRepository.update(orgId, logo);
      onMethodSaved?.(saved);
      return await logoRepository.resize(orgId, {
        ...saved,
        widthMm: size.widthMm,
        heightMm: size.heightMm,
      } as BaseLogo);
    }

    return withClampToasts<BaseLogo>(
      transaction,
      t("logoList.resizingLogo"),
      t("logoList.logoResized"),
      t("logoList.logoResizeFailed"),
      (logo) => logo
    );
  }

  function handleCommitLogo(logo: BaseLogo, commit: LogoCommit) {
    async function transaction() {
      return await logoRepository.commit(logo, commit);
    }

    return withToasts(
      transaction,
      t("logoList.committingLogo"),
      t("logoList.logoCommitted"),
      t("logoList.logoCommitFailed"),
      setIsLoading
    );
  }

  function handleSaveThenSkipLogo(orgId: number, logo: BaseLogo) {
    async function transaction() {
      const updatedLogo = await logoRepository.update(orgId, logo);
      return await logoRepository.initiateApproval(updatedLogo, {
        skip: true,
        email: undefined,
      });
    }

    // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: the approval start scales
    // a draft above its method's max size down instead of refusing it; the
    // answer is the final size and one toast says what happened.
    return withClampToasts<BaseLogo>(
      transaction,
      t("logoList.committingLogo"),
      t("logoList.logoCommitted"),
      t("logoList.logoCommitFailed"),
      (logo) => logo
    );
  }

  type DeleteLogoOptions = {
    silent?: boolean;
  };

  async function handleDeleteLogo(logo: BaseLogo, options?: DeleteLogoOptions) {
    async function transaction() {
      return await logoRepository.delete(getLogoOrgId(logo), logo.id);
    }

    return withToasts(
      transaction,
      t("logoList.deletingLogo"),
      t("logoList.logoDeleted"),
      "",
      options?.silent ? undefined : setIsLoading
    );
  }

  // Today's two requests: POST organizations/image-logo (the empty draft)
  // then PUT organizations/{orgId}/image-logo/{logoId} (the file). The
  // upload answer is the one the caches are seeded from.
  async function createThenUploadLogo(
    file: File,
    customerId: Organization["id"],
    title: string
  ): Promise<BaseLogo> {
    const baseLogo = await logoRepository.createBaseLogo(customerId, title);
    return await logoRepository.uploadFileToBaseLogo(
      file,
      customerId,
      baseLogo.id
    );
  }

  // Flag REACT_APP_UPLOAD_SINGLE_REQUEST (§3C): ONE multipart POST
  // organizations/{orgId}/image-logo (file, title, customerId) answering
  // the same LogoDto as the PUT. While the API's own flag is off (or the
  // route is not deployed) it answers 404 without a body (or with the
  // flag-off envelope { disabled, flag }): then today's two requests are
  // made for this upload and the tab remembers it for 15 min
  // (ROUTE_MISSING_TTL_MS), so the FE flag can go on before the API's
  // without a single failed upload. Every other error (a 404 WITH a
  // message, app117's 500 "Logo size is too big", the admin-without-
  // customerId 400) is the upload's outcome and is rethrown untouched -
  // same toast, dialog stays open, nothing seeded.
  // Flag off = createThenUploadLogo straight away, byte-identical wire.
  async function uploadNewLogo(
    file: File,
    customerId: Organization["id"],
    title: string
  ): Promise<BaseLogo> {
    if (shouldTrySingleRequestUpload()) {
      try {
        return await logoRepository.createAndUploadBaseLogo(
          file,
          customerId,
          title
        );
      } catch (error) {
        if (!isSingleRequestRouteMissing(error)) throw error;
        markSingleRequestRouteMissing();
      }
    }
    return await createThenUploadLogo(file, customerId, title);
  }

  // `previewUrl` (flag REACT_APP_UPLOAD_PENDING_ENABLED): the object URL the
  // caller already shows for the file (the dialog / the empty editor paint
  // it from t~0); undefined = none made yet (the design editor's panel) -
  // one is created here if the file is a browser-renderable image. Only
  // consulted for a worker-owned answer; otherwise the caller keeps owning
  // and revoking it as today.
  function handleCreateLogo(
    file: File,
    customerId: Organization["id"],
    title: string,
    previewUrl?: string | null
  ) {
    async function transaction() {
      const uploaded = await uploadNewLogo(file, customerId, title);
      // Clarity event logo_uploadet (flag REACT_APP_CLARITY_EVENTS_ENABLED):
      // the file is uploaded and a BaseLogo came back. This one place
      // covers /logo/create, the customer list dialog and the design
      // editor's side panel.
      trackLogoUploaded();
      // This path invalidates NOTHING today (unlike createImageLogo), so the
      // logo lists never learn about the new logo until their own staleTime
      // runs out. Mark every logo query stale without forcing refetches -
      // active screens pick it up on their next natural fetch. The detail
      // heal for the new logo itself is handled by useHealPendingHighRes.
      void queryClient.invalidateQueries({
        queryKey: ["logos"],
        refetchType: "none",
      });
      // "Upload stands alone" (flag REACT_APP_UPLOAD_PENDING_ENABLED): the
      // API stored original + row and handed the supplier work to its
      // worker (uploadPending + kind "upload": no serial, 0 mm, no colours).
      // The user's own file IS the artwork, so it becomes the editor's seed
      // (seedPendingUpload: object URL as a sharp, NOT pending bitmap for
      // raster; the answer as it is - file card - for vector) and the File +
      // object URL are kept (pendingUploadFiles) for the file card, "Upload
      // another file" and the release when the genuine high-res lands. A
      // finished answer (the supplier was up) takes today's path exactly.
      // Flag off = the predicate is false = nothing here runs.
      const seeded = isUploadKindPending(uploaded)
        ? seedWorkerOwnedUpload(uploaded, file, previewUrl)
        : uploaded;
      // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: seed the detail entry with the
      // UPLOAD response (mapped; it carries the real mm + low-res) so the
      // editor's heal subscription mounts on a warm cache - 0 extra GetLogo -
      // and the customer-list path (navigate to /logo/create?logo=) no longer
      // fires its own GetLogo on mount. The CREATE response (step 1) must
      // never seed: it has widthMm=heightMm=0 and no image.
      if (enviroment.logoHealPerMutationEnabled && uploaded?.id) {
        queryClient.setQueryData(
          ["logos", actAsCacheKey(), uploaded.id],
          seeded
        );
      }
      return seeded;
    }

    return withToasts(
      transaction,
      t("logoList.creatingLogo"),
      t("logoList.logoCreated"),
      t("logoList.logoCreateFailed"),
      setIsLoading
    );
  }

  // The release hook scrubs the object URL out of the detail entry the seed
  // below is written to (dropRevokedUploadUrl) the moment the entry is
  // dropped - Cancel / another logo / eviction / the row removed - so a
  // re-open while still pending never paints a revoked URL (skeptic round
  // 2). The key is captured now, under the act-as scope the seed uses.
  function seedWorkerOwnedUpload(
    uploaded: BaseLogo,
    file: File,
    previewUrl: string | null | undefined
  ): BaseLogo {
    const url = previewUrl === undefined ? previewUrlForFile(file) : previewUrl;
    const key = logoDetailKey(uploaded.id);
    registerPendingUploadFile(uploaded.id, file, url, () =>
      dropRevokedUploadUrl(queryClient, key, url)
    );
    return seedPendingUpload(uploaded, url);
  }

  // "Upload another file" / "Back to logos" (flag
  // REACT_APP_UPLOAD_PENDING_ENABLED): the dead row of a rejected upload is
  // removed with ONE silent DELETE - the repository drops it from every
  // cached list and detail entry first, exactly as the popup's delete does;
  // no loading / success toast (the dialog already said what happened), a
  // failure is logged and the caches restored by the repository. The held
  // File / object URL is released first.
  async function handleRemoveRejectedUpload(logo: BaseLogo): Promise<void> {
    releasePendingUploadFile(logo.id);
    try {
      await logoRepository.delete(getLogoOrgId(logo), logo.id);
    } catch (error) {
      console.error("Failed to remove the rejected upload:", error);
    }
  }

  function handleCreateTextLogo(
    value: string,
    color: string,
    heightMm: number,
    font: string,
    orgId: Organization["id"],
    customerId?: number,
    method?: string
  ): Promise<BaseLogo> {
    async function transaction() {
      const created = await logoRepository.createTextLogo(
        value,
        color,
        heightMm,
        font,
        orgId,
        customerId,
        method
      );
      trackLogoCreated();
      return created;
    }

    return withToasts(
      transaction,
      t("logoList.creatingTextLogo"),
      t("logoList.textLogoCreated"),
      t("logoList.textLogoCreateFailed"),
      setIsLoading
    );
  }

  function handleCreatePlaceholderNameLogo(
    color: string,
    heightMm: number,
    font: string,
    orgId: Organization["id"],
    customerId?: number,
    method?: string
  ): Promise<BaseLogo> {
    async function transaction() {
      const created = await logoRepository.createNameLogo(
        color,
        heightMm,
        font,
        orgId,
        customerId,
        method
      );
      trackLogoCreated();
      return created;
    }

    return withToasts(
      transaction,
      t("logoList.creatingNameLogo"),
      t("logoList.nameLogoCreated"),
      t("logoList.nameLogoCreateFailed"),
      setIsLoading
    );
  }

  function handleCreatePlaceholderNumberLogo(
    color: string,
    heightMm: number,
    font: string,
    method: string,
    orgId: Organization["id"],
    customerId?: number
  ): Promise<BaseLogo> {
    async function transaction() {
      const created = await logoRepository.createNumberLogo(
        color,
        heightMm,
        font,
        method,
        orgId,
        customerId
      );
      trackLogoCreated();
      return created;
    }

    return withToasts(
      transaction,
      t("logoList.creatingNumberset"),
      t("logoList.numbersetCreated"),
      t("logoList.numbersetCreateFailed"),
      setIsLoading
    );
  }

  function handlePlaceVerdict(logo: BaseLogo, verdict: Verdict) {
    async function transaction() {
      return await logoRepository.placeVerdict(logo, verdict);
    }

    // Button lock (flag approvalPatchEnabled): isLoading reaches the popup's
    // overlay and ApprovalVoting, so Approve/Reject cannot be clicked twice
    // while the PUT (prod p50 1.8 s, p95 3.1 s) is in flight. Flag off =
    // no isLoading, as today (toast only, buttons stay clickable).
    return withToasts(
      transaction,
      t("logoList.updatingApproval"),
      t("logoList.approvalSuccessful"),
      t("logoList.approvalFailed"),
      enviroment.approvalPatchEnabled ? setIsLoading : undefined
    );
  }

  function handleRejectAndDelete(logo: BaseLogo, note?: string) {
    async function transaction() {
      const placedVerdict = await logoRepository.placeVerdict(logo, {
        approve: false,
        rejectionNote: note ?? "",
      });
      return await logoRepository.delete(
        getLogoOrgId(placedVerdict),
        placedVerdict.id
      );
    }

    return withToasts(
      transaction,
      t("logoList.deleting"),
      t("logoList.deletionSuccessful"),
      t("logoList.deletionFailed"),
      setIsLoading
    );
  }

  // "Reject and fix it myself" for a logo (flag logoRejectAndEditEnabled,
  // Nichlas 17/10): today's ONE verdict PUT with editAfterReject:true - the
  // repository lands the answer in the caches exactly as every verdict (0
  // extra calls) - then straight to the editor of the SAME logo
  // (handleGoToEditLogo, the route Edit uses), where method, colours and
  // size are changed. The note is optional (the API stores a fixed internal
  // note for a blank one) and no e-mail is sent by either side. The editor
  // opens ONLY on the API's editAfterRejectApplied:true - false (not the
  // dealer's own rejection) AND a missing field (API flag
  // LogoRejectAndEditEnabled off / older API, which ignored the request and
  // stored a blank note blank) = rejected as today: one error toast, no
  // navigation - so a wrong flag order is visible instead of silent (fix
  // after skeptic 17/10). A failed PUT has shown its toast (withToasts) and
  // never navigates. Same button lock as a plain verdict
  // (approvalPatchEnabled).
  function handleRejectAndEdit(logo: BaseLogo, note: string): Promise<void> {
    return withToasts(
      () =>
        logoRepository.placeVerdict(logo, {
          approve: false,
          rejectionNote: note,
          editAfterReject: true,
        }),
      t("logoList.updatingApproval"),
      t("logoList.approvalSuccessful"),
      t("logoList.approvalFailed"),
      enviroment.approvalPatchEnabled ? setIsLoading : undefined,
      { showSuccess: (rejected) => rejected.editAfterRejectApplied === true }
    )
      .then((rejected) => {
        if (rejected.editAfterRejectApplied !== true) {
          useErrorToast(t("compDialogsInput.rejectAndEditEditorFailed"));
          return;
        }
        handleGoToEditLogo(logo);
      })
      .catch(() => {});
  }

  // A server-side copy re-derives the artwork's natural size instead of keeping the
  // source logo's saved dimensions, so clones opened mis-sized in the editor (image
  // overflowing the rulers). Restore the source's saved size on the copy — but never
  // fail the copy flow over it: a fresh serial may not be resizable yet, and the
  // user can still resize manually.
  async function tryRestoreCopiedLogoSize(
    source: BaseLogo,
    copy: BaseLogo
  ): Promise<BaseLogo> {
    const hasValidSize =
      (source.widthMm ?? 0) > 0 && (source.heightMm ?? 0) > 0;
    const sizeDiffers =
      source.widthMm !== copy.widthMm || source.heightMm !== copy.heightMm;
    if (!hasValidSize || !sizeDiffers) return copy;
    // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: the copy answer says the
    // server scaled the clone down (maxSizeClamp) - a resize to the
    // source's size would only be scaled down again and cost one supplier
    // call.
    if (isCloneSizeClampedByServer(copy)) return copy;

    try {
      return await logoRepository.resize(getLogoOrgId(copy), {
        ...copy,
        widthMm: source.widthMm,
        heightMm: source.heightMm,
      } as BaseLogo);
    } catch {
      return copy;
    }
  }

  // Flag REACT_APP_CLONE_INSTANT_ENABLED: the API's instant clone answers
  // with the source's size, colours and prices, marked uploadPending until
  // its worker has fetched the clone's own supplier identity. Such a
  // response IS the editor's seed: no size restore (the mm are the source's
  // already - and a resize would meet the clone-upload guard) and no GetLogo
  // from this flow (the "Preparing logo…" re-read covers the worker's
  // result). Judged on the stamp ALONE: the response's highResUrl is a
  // data-URI that differs from lowResUrl whether it is the source's genuine
  // high-res or only the 180px processed blob (the same trap the heal's
  // equality rule documents), so the image is never a signal here - see
  // seedInstantClone for the bitmap. Flag off = always false.
  function isInstantCloneSeed(newLogo: BaseLogo): boolean {
    return isLogoUploadPending(newLogo);
  }

  // The instant clone's canvas bitmap. The clone's high-res IS a copy of
  // the source's, so the source's genuine high-res (the popup's GetLogo
  // cached it, or the list/popup object carries it) is overlaid without
  // loss - and without a pending mark: no render is owed, so 0 heal polls.
  // No source high-res (the source had none, or only the list stub is at
  // hand): the response is kept and, as in today's clone flow, stamped
  // pending under REACT_APP_LOGO_HEAL_PER_MUTATION so the editor's heal
  // polls for the clone's own render (max 3 GetLogo) instead of trusting a
  // possibly upscaled 180px. Always seeds the detail entry the editor
  // mounts on and returns what it holds.
  function seedInstantClone(source: BaseLogo, newLogo: BaseLogo): BaseLogo {
    const detailKey = logoDetailKey(newLogo.id);
    const sharpUrl = sourceSharpUrl(
      queryClient.getQueryData<BaseLogo>(logoDetailKey(source.id)),
      source
    );
    if (sharpUrl) {
      const seeded = withSharpPlaceholder(newLogo, sharpUrl, false);
      queryClient.setQueryData(detailKey, seeded);
      return seeded;
    }
    if (enviroment.logoHealPerMutationEnabled) {
      markDetailHighResPending(queryClient, newLogo.id);
      return queryClient.getQueryData<BaseLogo>(detailKey) ?? newLogo;
    }
    return newLogo;
  }

  function handleCopyLogo(logo: BaseLogo) {
    // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: the copy ANSWER (not what
    // the flow returns - a later resize answer has no clone marker) says
    // whether the server scaled the clone down.
    let copyAnswer: BaseLogo | undefined;
    async function transaction() {
      const newLogo = await logoRepository.copy(getLogoOrgId(logo), logo.id);
      copyAnswer = newLogo;
      // Instant clone (flag): the response with the source's bitmap
      // overlaid is the seed - no size restore.
      if (isInstantCloneSeed(newLogo)) return seedInstantClone(logo, newLogo);
      const restored = await tryRestoreCopiedLogoSize(logo, newLogo);
      // Flag REACT_APP_LOGO_HEAL_PER_MUTATION: the copy/resize responses carry
      // the 180px processed blob as highResUrl, which the heal's equality
      // rule never classifies as pending - stamp the seeded entry so the
      // design editor's heal polls for the real render (max 3 GetLogo).
      // AFTER the size restore: its resize response re-seeds the entry and
      // would wipe a stamp set before it.
      if (enviroment.logoHealPerMutationEnabled) {
        markDetailHighResPending(queryClient, restored.id);
      }
      // Flag REACT_APP_LOGO_KEEP_SHARP (F3): the clone is the same artwork
      // as its source, whose high-res the design already holds (or the
      // popup's GetLogo cached) - open the tool on THAT bitmap, marked
      // pending so the heal polls for the clone's own render, instead of
      // the response's upscaled 180px. No source high-res = as today.
      if (enviroment.logoKeepSharpEnabled) {
        const sharpUrl = sourceSharpUrl(
          queryClient.getQueryData<BaseLogo>(logoDetailKey(logo.id)),
          logo
        );
        const seeded = withSharpPlaceholder(restored, sharpUrl, true);
        if (seeded !== restored) {
          queryClient.setQueryData(logoDetailKey(restored.id), seeded);
          return seeded;
        }
      }
      return restored;
    }

    return withClampToasts<BaseLogo>(
      transaction,
      t("logoList.copyingLogo"),
      t("logoList.copyingSuccessful"),
      t("logoList.copyingFailed"),
      () => copyAnswer
    );
  }

  function handleCopyThenGoToLogo(logo: BaseLogo) {
    let copyAnswer: BaseLogo | undefined;
    async function transaction() {
      // Warm the editor's blocking org-list queries while the copy call runs:
      // the editor screen gates its ENTIRE render on all-customers/all-vendors
      // (useCustomerAndVendors) with a fullscreen spinner on a cold cache.
      // Firing the prefetches now lets the multi-second server-side copy pay
      // for them. Fail-silent cache warming with the exact key the screen uses
      // (getAllQuery) - same idiom as the login prefetch wave in App.tsx.
      organizationRepository.prefetchAllCustomers(getAllQuery);
      organizationRepository.prefetchAllVendors(vendorLookupQuery());

      const newLogo = await logoRepository.copy(getLogoOrgId(logo), logo.id);
      copyAnswer = newLogo;

      // Instant clone (flag REACT_APP_CLONE_INSTANT_ENABLED): open the
      // editor on the response with the source's bitmap overlaid
      // (seedInstantClone). 0 further calls from this flow.
      if (isInstantCloneSeed(newLogo)) {
        seedInstantClone(logo, newLogo);
        navigate({
          pathname: "/logo/create",
          search: createSearchParams({
            ["logo"]: "" + newLogo.id,
            ["organization"]: "" + getLogoOrgId(newLogo),
          }).toString(),
        });
        return;
      }

      // Detail cache key under the CURRENT act-as scope (["logos", actAs,
      // id] — same shared helper as LogoRepository.useLogo, which the editor
      // reads). Captured ONCE so the optimistic seed, the failed-restore heal
      // and the hi-res invalidation below all target the SAME entry even if
      // the admin switches identity while the ~6s background restore runs.
      const detailKey = ["logos", actAsCacheKey(), newLogo.id];

      // The size restore (tryRestoreCopiedLogoSize) used to be AWAITED here,
      // holding the user in the popup for the full resize round-trip (~6s
      // measured server-side) even though it is purely cosmetic size recovery.
      // Navigate as soon as the copy lands and run the restore in the
      // background instead. Two things make that safe:
      // 1) The editor freezes preselectedLogo/logoForm in useState at mount,
      //    so a resize finishing after navigation would never reach it anyway.
      //    We seed the detail cache with the SOURCE's saved dims up front (the
      //    exact values the resize will confirm), so the editor opens with the
      //    right size immediately.
      // 2) tryRestoreCopiedLogoSize never throws (own catch) and resize() runs
      //    seedAndMarkStale, so the cache converges on the server-confirmed
      //    state when the background call lands.
      // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED: when the copy answer
      // says the server scaled the clone down (maxSizeClamp) the editor
      // opens on the clone's OWN size - no source-dims seed and no restore.
      // Flag off = always false = today's seed.
      const hasValidSize =
        !isCloneSizeClampedByServer(newLogo) &&
        (logo.widthMm ?? 0) > 0 &&
        (logo.heightMm ?? 0) > 0;
      // Flag REACT_APP_LOGO_KEEP_SHARP (F3): the editor opens on the
      // SOURCE's high-res (the popup's GetLogo already cached it - 0 calls)
      // instead of the copy response's upscaled 180px, and keeps it until
      // the clone's own render lands (F1 guard in the editor). The pending
      // mark that makes the editor's heal poll is only set when no size
      // restore will follow: a restore re-seeds the entry with its own
      // pending response, which starts the heal exactly as today. No source
      // high-res = today's seed and today's calls.
      const keepSharp = enviroment.logoKeepSharpEnabled;
      const sharpUrl = keepSharp
        ? sourceSharpUrl(
            queryClient.getQueryData<BaseLogo>(logoDetailKey(logo.id)),
            logo
          )
        : undefined;
      const willRestoreSize =
        hasValidSize &&
        (logo.widthMm !== newLogo.widthMm ||
          logo.heightMm !== newLogo.heightMm);
      if (sharpUrl) {
        queryClient.setQueryData(
          detailKey,
          withSharpPlaceholder(
            hasValidSize
              ? { ...newLogo, widthMm: logo.widthMm, heightMm: logo.heightMm }
              : newLogo,
            sharpUrl,
            !willRestoreSize
          )
        );
      } else if (hasValidSize) {
        queryClient.setQueryData(detailKey, {
          ...newLogo,
          widthMm: logo.widthMm,
          heightMm: logo.heightMm,
        });
      }
      navigate({
        pathname: "/logo/create",
        search: createSearchParams({
          ["logo"]: "" + newLogo.id,
          ["organization"]: "" + getLogoOrgId(newLogo),
        }).toString(),
      });

      // Fire-and-forget by design (see above). Pass the server's copy (not the
      // optimistic seed) so the sizeDiffers check compares real server state.
      // If the resize FAILS (its own catch returns the unresized copy — a fresh
      // serial may not be resizable yet), heal the cache back to the server
      // truth: the optimistic source-dims seed must never outlive a failed
      // resize, or every cache reader would show a size the server never had.
      void tryRestoreCopiedLogoSize(logo, newLogo).then((restored) => {
        if (restored === newLogo) {
          // F3: server truth for the size, the source's bitmap kept (and
          // marked pending: no resize response will start the heal now).
          queryClient.setQueryData(
            detailKey,
            sharpUrl ? withSharpPlaceholder(newLogo, sharpUrl, true) : newLogo
          );
        }
        // The copy/resize responses only carry the 180px images, and the
        // seeded detail cache never refetches on its own (refetchOnMount is
        // off globally) — so the popup/editor would show a blurry stretched
        // icon indefinitely. Once the size restore settles, force one real
        // GetLogo in the background: it live-renders the sharp hi-res (and
        // carries the replayed colors), and every cache reader glides over.
        // With REACT_APP_LOGO_HEAL_PER_MUTATION the editor's own heal takes over
        // from THAT response if it still lacks a genuine high-res (max 3 more
        // GetLogo) - no pending stamp here: the restore's re-seed would wipe
        // it, and this GetLogo already covers the common case.
        // Flag REACT_APP_LOGO_KEEP_SHARP (F3): with the source's bitmap on
        // the canvas this GetLogo only earns its keep when the restore left
        // the entry pending (its resize response: the render for the new
        // key was just queued). Otherwise the pending-marked seed already
        // makes the editor's heal poll - skipping it is -1 GetLogo per clone.
        // Only under REACT_APP_LOGO_HEAL_PER_MUTATION: the pending mark is
        // honoured by isHighResPending only then, so without the heal flag
        // this GetLogo stays the one call that ever orders the clone's own
        // render.
        if (
          !sharpUrl ||
          !enviroment.logoHealPerMutationEnabled ||
          isHighResPending(restored)
        ) {
          void queryClient.invalidateQueries({
            queryKey: detailKey,
            refetchType: "all",
          });
        }
      });
    }

    return withClampToasts<void>(
      transaction,
      t("logoList.copyingLogo"),
      t("logoList.copyingSuccessful"),
      t("logoList.copyingFailed"),
      () => copyAnswer
    );
  }

  // Flag REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED - the four approval starts
  // below: the API scales a KitMaker draft above its method's max size down
  // (it no longer refuses it) and then approves it. The repository writes
  // the answer - the FINAL size - to the logo's detail entry and refetches
  // the lists, so the form / the list show it; the answer's maxSizeClamp
  // turns the success toast into ONE toast that also says the size changed.
  function handleInitiateApproval(logo: BaseLogo, approval: InitiateApproval) {
    let approvalAnswer: BaseLogo | undefined;
    async function transaction() {
      const approvedLogo = await logoRepository.initiateApproval(
        logo,
        approval
      );
      approvalAnswer = approvedLogo;
      // return await logoRepository.placeVerdict(approvedLogo, { approve: true })
    }

    return withClampToasts<void>(
      transaction,
      t("logoList.updatingApproval"),
      t("logoList.approvalSuccessful"),
      t("logoList.approvalFailed"),
      () => approvalAnswer
    );
  }

  function handleSkipApproval(logo: BaseLogo) {
    async function transaction() {
      return await logoRepository.initiateApproval(logo, {
        skip: true,
        email: undefined,
      });
    }

    return withClampToasts<BaseLogo>(
      transaction,
      t("logoList.skippingApproval"),
      t("logoList.approvalSkipSuccessful"),
      t("logoList.approvalSkipFailed"),
      (approved) => approved
    );
  }

  function handleSendApproval(logo: BaseLogo, email: string) {
    async function transaction() {
      return await logoRepository.sendApproval(logo, email);
    }

    return withToasts(
      transaction,
      t("logoList.sendingEmail"),
      t("logoList.emailSendSuccessful"),
      t("logoList.emailSendFailed"),
      setIsLoading
    );
  }

  function handleSendApprovalWithEndCustomerPending(
    logo: BaseLogo,
    email: string
  ) {
    async function transaction() {
      // Use conditional approval that preserves existing approval statuses
      return await logoRepository.sendApprovalConditional(logo, email);
    }

    return withToasts(
      transaction,
      t("logoList.sendingApproval"),
      t("logoList.approvalSendSuccessful"),
      t("logoList.approvalSendFailed"),
      setIsLoading
    );
  }

  function handleCommitAndInitiateApproval(
    logo: BaseLogo,
    commit: LogoCommit,
    approval: InitiateApproval
  ) {
    let approvalAnswer: BaseLogo | undefined;
    async function transaction() {
      // Flag approvalListFlowFixEnabled: skip the dead commit call (see
      // initiateApprovalFlow). Flag off = commit first, as today.
      approvalAnswer = await runCommitAndInitiateApproval(
        logoRepository,
        logo,
        commit,
        approval,
        enviroment.approvalListFlowFixEnabled
      );
      // return await logoRepository.placeVerdict(approvedLogo, { approve: true });
    }

    return withClampToasts<void>(
      transaction,
      t("logoList.updatingApproval"),
      t("logoList.approvalSuccessful"),
      t("logoList.approvalFailed"),
      () => approvalAnswer
    );
  }

  function handleSkipAddToCart(logo: BaseLogo, quantity: number) {
    if (cartHidden) {
      toast.error(t("logoList.cartNotAvailable"));
      return Promise.reject(new Error("Cart hidden"));
    }
    // Oversize sheet guard: the approval was skipped but the logo was NOT
    // added (its dialog is open) - no "Approval skipped and added to cart".
    let refusedByGuard = false;
    async function transaction() {
      return await logoRepository
        .initiateApproval(logo, {
          skip: true,
          email: undefined,
        })
        .then((updatedLogo) => {
          refusedByGuard = refusedAll(handleAddToCart(updatedLogo, quantity));
          return updatedLogo;
        });
    }

    return withClampToasts<BaseLogo>(
      transaction,
      t("logoList.skippingApprovalAddingToCart"),
      t("logoList.approvalSkippedAddedToCart"),
      t("logoList.approvalSkipFailed"),
      (approved) => approved,
      () => !refusedByGuard
    );
  }

  return {
    isLoading,
    handleGoToCreateNewLogo,
    handleAddToCart,
    handleAddItemsToCart,
    handleGoToEditLogo,
    handleDeleteLogo,
    handleChangeTitle,
    handlePlaceVerdict,
    handleRejectAndDelete,
    handleRejectAndEdit,
    handleCreateLogo,
    handleRemoveRejectedUpload,
    handleCreateTextLogo,
    handleCreatePlaceholderNameLogo,
    handleCreatePlaceholderNumberLogo,
    handleUpdateLogoColor,
    handleUpdateLogo,
    handleResizeLogo,
    handleSaveMethodThenResizeLogo,
    handleCopyThenGoToLogo,
    handleCopyLogo,
    handleCommitLogo,
    handleSaveThenSkipLogo,
    handleInitiateApproval,
    handleCommitAndInitiateApproval,
    handleSkipApproval,
    handleSendApproval,
    handleSendApprovalWithEndCustomerPending,
    handleSkipAddToCart,
  };
}

export interface ChangeLogoColors {
  colorChanges: ColorChange[];
}

export interface InitiateApproval {
  skip?: boolean;
  email?: string;
}

export interface ColorChange {
  from: string;
  to: string;
}
